import * as React from 'react';
import { Form, Formik } from 'formik';
import {
  ReportActions,
  ReportCheckboxGrid,
  ReportDateRange,
  ReportDescription,
  ReportSection,
  ReportToggle,
  ReportToggleList
} from 'Src/views/GenericView/AdminView/TeamsAndEvents/Events/SchoolEventView/Reports/ReportFormUi';
import 'Src/views/GenericView/AdminView/Clubs/Clubs/Clubs.css';

export interface UnionSchoolReportToggle {
  field: string;
  label: string;
  hint: string;
}

interface Props {
  initialValues: any;
  reportType: string;
  description: React.ReactNode;
  onCloseClick: () => void;
  onSubmit: (data: any, reportType: string) => void;
  toggles: UnionSchoolReportToggle[];
  showAudience?: boolean;
}

export function UnionSchoolReportForm({
  initialValues,
  reportType,
  description,
  onCloseClick,
  onSubmit,
  toggles,
  showAudience = true
}: Props) {
  return (
    <Formik initialValues={initialValues} onSubmit={values => onSubmit(values, reportType)}>
      {({ values, setFieldValue }) => {
        const selectedAges = (values.ageGroups || []).filter(item => item.isChecked);
        const selectedGenders = (values.genders || []).filter(item => item.isChecked);
        const audienceValid = !showAudience || (selectedAges.length > 0 && selectedGenders.length > 0);
        const toggleItem = (field: 'ageGroups' | 'genders', index: number) => {
          const items = values[field].map((item, itemIndex) =>
            itemIndex === index ? { ...item, isChecked: !item.isChecked } : item
          );
          setFieldValue(field, items);
          if (field === 'ageGroups') setFieldValue('isSelectAllAges', items.every(item => item.isChecked));
        };
        const setAllAges = (checked: boolean) => {
          setFieldValue('ageGroups', values.ageGroups.map(item => ({ ...item, isChecked: checked })));
          setFieldValue('isSelectAllAges', checked);
        };

        return (
          <Form className="averageClubAttendanceForm unionSchoolReportForm">
            <div className="averageClubAttendanceForm__scroll">
              <ReportDescription>{description}</ReportDescription>
              <ReportSection number={1} title="Reporting period" hint="Choose the date range covered by the report." required>
                <ReportDateRange
                  dateFrom={values.dateFrom}
                  dateTo={values.dateTo}
                  onChangeFrom={date => {
                    setFieldValue('dateFrom', date);
                    if (values.dateTo < date) setFieldValue('dateTo', date);
                  }}
                  onChangeTo={date => setFieldValue('dateTo', date)}
                />
              </ReportSection>
              {showAudience ? (
                <>
                  <ReportSection number={2} title="Gender" hint="Select at least one gender category." required>
                    <ReportCheckboxGrid items={values.genders} onChange={index => toggleItem('genders', index)} />
                  </ReportSection>
                  <ReportSection
                    number={3}
                    title="Age group"
                    hint="Select the age groups included in the report."
                    required
                    action={
                      <button
                        type="button"
                        onClick={() => setAllAges(selectedAges.length !== values.ageGroups.length)}
                      >
                        {selectedAges.length === values.ageGroups.length ? 'Clear all' : 'Select all'}
                      </button>
                    }
                  >
                    <ReportCheckboxGrid items={values.ageGroups} onChange={index => toggleItem('ageGroups', index)} />
                    {!audienceValid ? <div className="eReportAudienceError">Select an age group and a gender.</div> : null}
                  </ReportSection>
                </>
              ) : null}
              {toggles.length ? (
                <ReportSection
                  number={showAudience ? 4 : 2}
                  title="Report content"
                  hint="Choose what should be included in the generated report."
                >
                  <ReportToggleList>
                    {toggles.map(toggle => (
                      <ReportToggle
                        key={toggle.field}
                        name={toggle.field}
                        label={toggle.label}
                        hint={toggle.hint}
                        value={Boolean(values[toggle.field])}
                        onChange={value => setFieldValue(toggle.field, value)}
                      />
                    ))}
                  </ReportToggleList>
                </ReportSection>
              ) : null}
            </div>
            <ReportActions onCancel={onCloseClick} disabled={!audienceValid} />
          </Form>
        );
      }}
    </Formik>
  );
}
